import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Lock, User, ArrowLeft, Loader2, Eye, EyeOff,
  ChevronRight, Shield, AlertCircle, KeyRound, ExternalLink
} from 'lucide-react';
import { LEGAL_DOC_URL, LEGAL_CONSENT_VERSION } from '../constants/legal';
import LanguageToggle from '../components/LanguageToggle';

const AuthEmail = () => {
  const { login, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';
  const es = language === 'es';
  // Trilingual literal picker for the handful of hardcoded strings below
  // that aren't in the i18n bundle (validation errors + legal consent).
  const L = (frTxt, enTxt, esTxt) => es ? esTxt : (fr ? frTxt : enTxt);

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Legal consent — Zéro Relais / Déploiement mondial. Mandatory for register.
  const [legalAccepted, setLegalAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'register') {
      if (password !== confirmPassword) {
        setError(L('Les mots de passe ne correspondent pas.', 'Passwords do not match.', 'Las contraseñas no coinciden.'));
        return;
      }
      if (password.length < 8) {
        setError(L('Le mot de passe doit contenir au moins 8 caracteres.', 'Password must be at least 8 characters.', 'La contraseña debe tener al menos 8 caracteres.'));
        return;
      }
      if (!name.trim()) {
        setError(L('Le nom est requis.', 'Name is required.', 'El nombre es obligatorio.'));
        return;
      }
      if (!legalAccepted) {
        setError(L(
          'Tu dois accepter les conditions legales pour creer un compte.',
          'You must accept the legal terms to create an account.',
          'Debes aceptar los términos legales para crear una cuenta.'
        ));
        return;
      }
    }

    setLoading(true);
    try {
      // Auto-detect ISO2 country from browser locale (e.g., "en-US" → "US", "fr-CA" → "CA").
      // Zero-friction: no extra form field. User can still override in Settings.
      const detectCountry = () => {
        try {
          const langs = [navigator.language, ...(navigator.languages || [])];
          for (const tag of langs) {
            if (!tag) continue;
            const parts = String(tag).split('-');
            if (parts.length >= 2 && parts[1].length === 2) return parts[1].toUpperCase();
          }
        } catch (_err) { /* empty */ }
        return null;
      };
      const endpoint = mode === 'register' ? '/auth/register' : '/auth/login-email';
      // 🌐 Auto-detected UI language (Iter. 383). Lives in i18nInstance.language
      // (already resolved by browser-languagedetector from navigator at boot).
      // We snap it to a 2-letter code in our supported set so a new signup
      // from a Spanish phone gets persisted as `language: "es"` server-side.
      const detectLang = () => {
        const raw = (language || navigator.language || 'fr').slice(0, 2).toLowerCase();
        return ['fr', 'en', 'es'].includes(raw) ? raw : 'fr';
      };
      const payload = mode === 'register'
        ? {
            email: email.trim().toLowerCase(),
            password,
            name: name.trim(),
            legal_consent_accepted: true,
            legal_consent_version: LEGAL_CONSENT_VERSION,
            country: detectCountry(),
            language: detectLang(),
          }
        : { email: email.trim().toLowerCase(), password };

      const res = await api.post(endpoint, payload);
      const data = res.data;

      // Store tokens
      const accessToken = data.access_token || data.session_token;
      if (accessToken) {
        localStorage.setItem('session_token', accessToken);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      updateUser(data.user);
      toast.success(L('Connexion réussie !', 'Login successful!', '¡Inicio de sesión exitoso!'));

      // Redirect
      const savedPath = sessionStorage.getItem('redirectAfterLogin');
      if (savedPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(savedPath, { replace: true, state: { user: data.user, fromAuth: true } });
      } else if (mode === 'register') {
        // New registration → Welcome Home page
        navigate('/welcome-home', { replace: true, state: { user: data.user, fromAuth: true } });
      } else if (data.user?.onboarding_complete) {
        // Existing onboarded users — gate behind Deep Chat then Day-7 intro
        if (data.user.role === 'client' && !data.user.deep_chat_completed) {
          navigate('/deep-chat', { replace: true, state: { user: data.user, fromAuth: true } });
        } else if (data.user.role === 'client' && !data.user.welcome_intro_accepted) {
          navigate('/welcome-challenge-intro', { replace: true, state: { user: data.user, fromAuth: true } });
        } else {
          navigate('/dashboard', { replace: true, state: { user: data.user, fromAuth: true } });
        }
      } else {
        navigate('/onboarding', { replace: true, state: { user: data.user, fromAuth: true } });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || L('Erreur de connexion.', 'Login error.', 'Error de conexión.');
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back button + Language toggle */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="auth-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {L('Retour', 'Back', 'Volver')}
          </button>
          <LanguageToggle />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/mascot-yuri.png" alt="YUMIX" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-foreground">
            {mode === 'login' ? t('auth.title_login') : t('auth.title_register')}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {mode === 'login' ? t('auth.subtitle_login') : t('auth.subtitle_register')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2.5 text-sm text-destructive"
                data-testid="auth-error"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (register only) */}
            <AnimatePresence>
              {mode === 'register' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="block text-xs text-muted-foreground mb-1.5 font-heading uppercase tracking-wider">
                    {L('Nom complet', 'Full name', 'Nombre completo')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder={L('Ton nom et prenom', 'Your full name', 'Tu nombre y apellido')}
                      className="pl-9 bg-secondary/50 border-border/50"
                      data-testid="auth-name-input"
                      autoComplete="name"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Email */}
            <div>
              <label className="block text-xs text-muted-foreground mb-1.5 font-heading uppercase tracking-wider">
                {t('auth.email_label')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('auth.email_placeholder')}
                  className="pl-9 bg-secondary/50 border-border/50"
                  data-testid="auth-email-input"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs text-muted-foreground mb-1.5 font-heading uppercase tracking-wider">
                {t('auth.password_label')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? L('Minimum 8 caracteres', 'Minimum 8 characters', 'Mínimo 8 caracteres') : '••••••••'}
                  className="pl-9 pr-10 bg-secondary/50 border-border/50"
                  data-testid="auth-password-input"
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (register only) */}
            <AnimatePresence>
              {mode === 'register' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="block text-xs text-muted-foreground mb-1.5 font-heading uppercase tracking-wider">
                    {L('Confirmer le mot de passe', 'Confirm password', 'Confirmar contraseña')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder={L('Retape ton mot de passe', 'Retype your password', 'Vuelve a escribir tu contraseña')}
                      className="pl-9 bg-secondary/50 border-border/50"
                      data-testid="auth-confirm-password-input"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Forgot password link (login mode only) */}
            {mode === 'login' && (
              <div className="text-right -mt-2">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  data-testid="auth-forgot-password-link"
                >
                  <KeyRound className="w-3 h-3 inline mr-1" />
                  {L('Mot de passe oublie ?', 'Forgot password?', '¿Contraseña olvidada?')}
                </button>
              </div>
            )}

            {/* Legal consent (register mode only) — mandatory */}
            <AnimatePresence>
              {mode === 'register' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-secondary/30 border border-border/60 rounded-xl p-3.5"
                >
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <Checkbox
                      checked={legalAccepted}
                      onCheckedChange={(v) => setLegalAccepted(Boolean(v))}
                      className="mt-0.5 shrink-0"
                      data-testid="auth-legal-consent-checkbox"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      {(() => {
                        // Trilingual legal-consent copy. Each language uses the
                        // same 3-link layout (Terms / Privacy / Medical Disclaimer).
                        const links = {
                          fr: { intro: "En m'inscrivant, j'accepte les ", terms: "Conditions Generales d'Utilisation", termsSuffix: " (incluant la clause de non-divulgation), la ", privacy: "Politique de Confidentialite", privacySuffix: " et l'", medical: "Avis de Non-Responsabilite Medicale", closing: "." },
                          en: { intro: "By signing up, I accept the ", terms: "Terms of Service", termsSuffix: " (including the non-disclosure clause), the ", privacy: "Privacy Policy", privacySuffix: " and the ", medical: "Medical Disclaimer", closing: "." },
                          es: { intro: "Al registrarme, acepto los ", terms: "Términos de Servicio", termsSuffix: " (incluida la cláusula de confidencialidad), la ", privacy: "Política de Privacidad", privacySuffix: " y el ", medical: "Aviso Médico", closing: "." },
                        };
                        const l = links[es ? 'es' : (fr ? 'fr' : 'en')];
                        return (
                          <>
                            {l.intro}
                            <a
                              href={LEGAL_DOC_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 inline-flex items-center gap-0.5"
                              data-testid="auth-legal-cgu-link"
                            >
                              {l.terms}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            {l.termsSuffix}
                            <a
                              href={LEGAL_DOC_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 inline-flex items-center gap-0.5"
                              data-testid="auth-legal-privacy-link"
                            >
                              {l.privacy}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            {l.privacySuffix}
                            <a
                              href={LEGAL_DOC_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 inline-flex items-center gap-0.5"
                              data-testid="auth-legal-disclaimer-link"
                            >
                              {l.medical}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            {l.closing}
                          </>
                        );
                      })()}
                    </span>
                  </label>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || (mode === 'register' && !legalAccepted)}
              className="w-full gap-2 font-heading uppercase tracking-wider h-12 text-base"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {mode === 'login' ? t('auth.submit_login') : t('auth.submit_register')}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground">{t('landing.or')}</span>
            </div>
          </div>

          {/* Google Sign In */}
          <Button
            variant="outline"
            onClick={login}
            className="w-full gap-2 h-11 text-sm"
            data-testid="auth-google-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('auth.google_continue')}
          </Button>

          {/* Toggle mode */}
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="text-sm text-primary hover:underline"
              data-testid="auth-toggle-mode"
            >
              {mode === 'login'
                ? `${t('auth.no_account')} ${t('auth.create_account')}`
                : `${t('auth.has_account')} ${t('auth.sign_in')}`}
            </button>
          </div>
        </div>

        {/* Security badge */}
        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5 text-green-500" />
          {t('auth.bcrypt_notice')}
        </div>
      </motion.div>
    </div>
  );
};

export default AuthEmail;
